import React, { useEffect, useState } from "react";
import { Button, Card, Form, InputGroup, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../../../services/api";

const PRICING_TYPES = [
  { value: "GROUP_BANDS", label: "Group size bands - one amount for the whole group" },
  { value: "PER_PERSON", label: "Per person" },
  { value: "PER_VEHICLE", label: "Per vehicle - jeep, bus, boat" },
  { value: "FIXED", label: "Fixed per booking" },
];

const blankTiers = () => [
  { minPeople: 1, maxPeople: 6, amount: 3000, displayOrder: 1 },
  { minPeople: 7, maxPeople: 7, amount: 3500, displayOrder: 2 },
  { minPeople: 8, maxPeople: "", amount: 6000, displayOrder: 3 },
];

const blank = {
  name: "",
  description: "",
  contactName: "",
  contactNumber: "",
  googleMapLink: "",
  includedDefault: false,
  basePrice: 0,
  sevenPersonPrice: 0,
  eightPlusPrice: 0,
  subtractAmount: 0,
  pricingType: "GROUP_BANDS",
  unitPrice: "",
  peoplePerVehicle: "",
  extraPeoplePerVehicle: "",
  extraPersonPrice: "",
  childrenFreeUpToAge: "",
  tiers: blankTiers(),
  options: [],
  displayOrder: 0,
  active: true,
};

const blankOption = (order) => ({
  name: "",
  description: "",
  pricingType: "PER_PERSON",
  unitPrice: "",
  peoplePerVehicle: "",
  extraPeoplePerVehicle: "",
  extraPersonPrice: "",
  childrenFreeUpToAge: "",
  tiers: [],
  contactName: "",
  contactNumber: "",
  googleMapLink: "",
  displayOrder: order,
  active: true,
});

export default function AddonsManager() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(blank);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/admin/pricing-addons");
      setRows(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const patch = (field, value) => {
    setEditing((prev) => ({ ...prev, [field]: value }));
  };

  const patchOption = (index, value) => {
    setEditing((prev) => ({
      ...prev,
      options: (prev.options || []).map((option, idx) => (idx === index ? value : option)),
    }));
  };

  const addOption = () => {
    setEditing((prev) => ({
      ...prev,
      options: [...(prev.options || []), blankOption((prev.options || []).length + 1)],
    }));
  };

  const removeOption = (index) => {
    setEditing((prev) => ({
      ...prev,
      options: (prev.options || []).filter((_, idx) => idx !== index),
    }));
  };

  const hasOptions = (editing.options || []).length > 0;

  const save = async () => {
    setSaving(true);
    try {
      const payload = normalize(editing);
      const { data } = payload.id
        ? await api.put(`/api/admin/pricing-addons/${payload.id}`, payload)
        : await api.post("/api/admin/pricing-addons", payload);
      setRows((prev) =>
        payload.id ? prev.map((row) => (row.id === data.id ? data : row)) : [...prev, data],
      );
      setEditing(blank);
      toast.success("Add-on saved");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not save the add-on");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this add-on?")) return;
    await api.delete(`/api/admin/pricing-addons/${id}`);
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  return (
    <Card className="shadow-sm">
      <Card.Header>
        <div className="fw-semibold">Add-ons Master</div>
        <div className="text-muted small">Create reusable add-ons, then select them in each trip's Dynamic Pricing.</div>
      </Card.Header>
      <Card.Body>
        <Card className="mb-3">
          <Card.Body>
            <div className="fw-semibold mb-3">{editing.id ? "Edit Add-on" : "New Add-on"}</div>
            <div className="row g-3">
              <div className="col-md-4">
                <Form.Label>Name</Form.Label>
                <Form.Control value={editing.name || ""} onChange={(e) => patch("name", e.target.value)} />
              </div>
              <div className="col-md-8">
                <Form.Label>Description</Form.Label>
                <Form.Control value={editing.description || ""} onChange={(e) => patch("description", e.target.value)} />
              </div>
              <div className="col-md-3">
                <Form.Label>Contact Name</Form.Label>
                <Form.Control value={editing.contactName || ""} onChange={(e) => patch("contactName", e.target.value)} placeholder="Shown in the driver message" />
              </div>
              <div className="col-md-3">
                <Form.Label>Contact Number</Form.Label>
                <Form.Control type="tel" value={editing.contactNumber || ""} onChange={(e) => patch("contactNumber", e.target.value)} />
              </div>
              <div className="col-md-6">
                <Form.Label>Maps Location</Form.Label>
                <Form.Control value={editing.googleMapLink || ""} onChange={(e) => patch("googleMapLink", e.target.value)} placeholder="https://maps.app.goo.gl/..." />
              </div>
              <div className="col-md-2">
                <Form.Label>Order</Form.Label>
                <Form.Control type="number" value={editing.displayOrder || 0} onChange={(e) => patch("displayOrder", Number(e.target.value || 0))} />
              </div>
              <div className="col-md-4 d-flex align-items-end gap-3">
                <Form.Check type="switch" label="Included default" checked={!!editing.includedDefault} onChange={(e) => patch("includedDefault", e.target.checked)} />
                <Form.Check type="switch" label="Active" checked={editing.active !== false} onChange={(e) => patch("active", e.target.checked)} />
              </div>
            </div>

            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <div className="fw-semibold">Options</div>
                  <div className="small text-muted">
                    For add-ons the customer chooses a kind of - a bus as Government, Non AC or AC. Each option is priced its own way; with none, the add-on is priced below.
                  </div>
                </div>
                <Button size="sm" variant="outline-primary" onClick={addOption}>
                  Add Option
                </Button>
              </div>
              {(editing.options || []).map((option, index) => (
                <div key={option.id || `new-${index}`} className="border rounded p-3 mb-2">
                  <div className="row g-2 align-items-end">
                    <div className="col-md-3">
                      <Form.Label className="small text-muted mb-1">Option name</Form.Label>
                      <Form.Control value={option.name || ""} onChange={(e) => patchOption(index, { ...option, name: e.target.value })} placeholder="e.g. AC" />
                    </div>
                    <div className="col-md-5">
                      <Form.Label className="small text-muted mb-1">Description (shown to customers)</Form.Label>
                      <Form.Control value={option.description || ""} onChange={(e) => patchOption(index, { ...option, description: e.target.value })} />
                    </div>
                    <div className="col-md-1">
                      <Form.Label className="small text-muted mb-1">Order</Form.Label>
                      <Form.Control type="number" value={option.displayOrder ?? index + 1} onChange={(e) => patchOption(index, { ...option, displayOrder: Number(e.target.value || 0) })} />
                    </div>
                    <div className="col-md-1">
                      <Form.Check type="switch" label="Active" checked={option.active !== false} onChange={(e) => patchOption(index, { ...option, active: e.target.checked })} />
                    </div>
                    <div className="col-md-2 text-end">
                      <Button size="sm" variant="outline-danger" onClick={() => removeOption(index)}>Remove Option</Button>
                    </div>
                  </div>
                  <PricingFields value={option} onChange={(value) => patchOption(index, value)} />
                  <div className="row g-2 mt-1">
                    <div className="col-md-3">
                      <Form.Label className="small text-muted mb-1">Contact name (optional)</Form.Label>
                      <Form.Control value={option.contactName || ""} onChange={(e) => patchOption(index, { ...option, contactName: e.target.value })} placeholder="Blank: the add-on's contact" />
                    </div>
                    <div className="col-md-3">
                      <Form.Label className="small text-muted mb-1">Contact number (optional)</Form.Label>
                      <Form.Control type="tel" value={option.contactNumber || ""} onChange={(e) => patchOption(index, { ...option, contactNumber: e.target.value })} />
                    </div>
                    <div className="col-md-6">
                      <Form.Label className="small text-muted mb-1">Maps location (optional)</Form.Label>
                      <Form.Control value={option.googleMapLink || ""} onChange={(e) => patchOption(index, { ...option, googleMapLink: e.target.value })} placeholder="Blank: the add-on's location" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {!hasOptions && (
              <div className="mt-3">
                <div className="fw-semibold mb-2">Pricing</div>
                <PricingFields value={editing} onChange={setEditing} />
              </div>
            )}

            {editing.includedDefault && (
              <div className="mt-3">
                {!hasOptions && (editing.pricingType || "GROUP_BANDS") === "GROUP_BANDS" ? (
                  <div className="row g-3">
                    <Money label="Subtract when declined" value={editing.subtractAmount} onChange={(value) => patch("subtractAmount", value)} />
                  </div>
                ) : (
                  <div className="small text-muted">
                    Included by default: a customer who declines it gets its price for their group taken off{hasOptions ? " (the first option's)" : ""}.
                  </div>
                )}
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button variant="outline-secondary" onClick={() => setEditing(blank)}>Clear</Button>
              <Button disabled={saving || !editing.name} onClick={save}>{saving ? "Saving..." : "Save Add-on"}</Button>
            </div>
          </Card.Body>
        </Card>

        {loading ? (
          <div className="py-5 text-center"><Spinner /></div>
        ) : (
          <Table responsive hover size="sm" className="align-middle">
            <thead>
              <tr><th style={{ width: 44 }}>#</th>
                <th>Name</th>
                <th>Pricing</th>
                <th>Included</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.id}><td className="small fw-semibold">{index + 1}</td>
                  <td>
                    <div className="fw-semibold">{row.name}</div>
                    <div className="small text-muted">{row.description}</div>
                    {(row.contactName || row.contactNumber) && (
                      <div className="small">Contact: {[row.contactName, row.contactNumber].filter(Boolean).join(" - ")}</div>
                    )}
                  </td>
                  <td>
                    {(row.options || []).length ? (
                      (row.options || []).map((option) => (
                        <div key={option.id} className={option.active === false ? "text-muted" : ""}>
                          <span className="fw-semibold">{option.name}:</span> {pricingSummary(option)}
                          {option.active === false ? " (inactive)" : ""}
                        </div>
                      ))
                    ) : (
                      pricingSummary(row)
                    )}
                  </td>
                  <td>{row.includedDefault ? "Yes" : "No"}</td>
                  <td>{row.active ? "Active" : "Inactive"}</td>
                  <td className="text-end">
                    {row.googleMapLink ? <Button as="a" href={row.googleMapLink} target="_blank" rel="noreferrer" size="sm" variant="outline-primary" className="me-2">Map</Button> : null}
                    <Button size="sm" variant="outline-secondary" className="me-2" onClick={() => setEditing(forEditing(row))}>Edit</Button>
                    <Button size="sm" variant="outline-danger" onClick={() => remove(row.id)}>Delete</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}

// The pricing type and the amounts it needs - shared by an add-on and each of its options.
function PricingFields({ value, onChange }) {
  const type = value.pricingType || "GROUP_BANDS";
  const set = (field, fieldValue) => onChange({ ...value, [field]: fieldValue });
  const tiers = value.tiers || [];
  const patchTier = (index, field, fieldValue) =>
    set("tiers", tiers.map((tier, idx) => (idx === index ? { ...tier, [field]: fieldValue } : tier)));

  return (
    <div className="mt-2">
      <div className="row g-2 align-items-end">
        <div className="col-md-4">
          <Form.Label className="small text-muted mb-1">Pricing type</Form.Label>
          <Form.Select value={type} onChange={(e) => set("pricingType", e.target.value)}>
            {PRICING_TYPES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </Form.Select>
        </div>
        {type === "PER_PERSON" && (
          <>
            <AmountField label="Price per person" value={value.unitPrice} onChange={(amount) => set("unitPrice", amount)} />
            <div className="col-md-3">
              <Form.Label className="small text-muted mb-1">Children free up to age (inclusive)</Form.Label>
              <Form.Control type="number" min="0" placeholder="Blank: children pay" value={value.childrenFreeUpToAge ?? ""} onChange={(e) => set("childrenFreeUpToAge", e.target.value === "" ? "" : Number(e.target.value))} />
            </div>
          </>
        )}
        {type === "PER_VEHICLE" && (
          <>
            <AmountField label="Price per vehicle" value={value.unitPrice} onChange={(amount) => set("unitPrice", amount)} />
            <div className="col-md-2">
              <Form.Label className="small text-muted mb-1">People per vehicle</Form.Label>
              <Form.Control type="number" min="1" value={value.peoplePerVehicle ?? ""} onChange={(e) => set("peoplePerVehicle", e.target.value === "" ? "" : Number(e.target.value))} />
            </div>
            <div className="col-md-2">
              <Form.Label className="small text-muted mb-1">Extra people per vehicle</Form.Label>
              <Form.Control type="number" min="0" placeholder="None" value={value.extraPeoplePerVehicle ?? ""} onChange={(e) => set("extraPeoplePerVehicle", e.target.value === "" ? "" : Number(e.target.value))} />
            </div>
            {Number(value.extraPeoplePerVehicle || 0) > 0 && (
              <AmountField label="Price per extra person" value={value.extraPersonPrice} onChange={(amount) => set("extraPersonPrice", amount)} />
            )}
          </>
        )}
        {type === "FIXED" && (
          <AmountField label="Price per booking" value={value.unitPrice} onChange={(amount) => set("unitPrice", amount)} />
        )}
      </div>
      {type === "PER_VEHICLE" && Number(value.extraPeoplePerVehicle || 0) > 0 && (
        <div className="small text-muted mt-1">
          A vehicle may take up to {value.extraPeoplePerVehicle} more {Number(value.extraPeoplePerVehicle) === 1 ? "person" : "people"} at the extra price; the cheaper of that or another vehicle is charged.
        </div>
      )}
      {type === "GROUP_BANDS" && (
        <div className="mt-2">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <div className="small fw-semibold">People bands - both ends inclusive</div>
            <Button size="sm" variant="outline-primary" onClick={() => set("tiers", [...tiers, { minPeople: 1, maxPeople: "", amount: 0, displayOrder: tiers.length + 1 }])}>
              Add Band
            </Button>
          </div>
          <Table responsive size="sm" className="align-middle mb-0">
            <thead>
              <tr><th style={{ width: 44 }}>#</th>
                <th>From people (inclusive)</th>
                <th>To people (inclusive)</th>
                <th>Amount for the group</th>
                <th>Order</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tiers.map((tier, index) => (
                <tr key={index}><td className="small fw-semibold">{index + 1}</td>
                  <td>
                    <Form.Control type="number" min="1" value={tier.minPeople ?? 1} onChange={(e) => patchTier(index, "minPeople", Number(e.target.value || 1))} />
                  </td>
                  <td>
                    <Form.Control type="number" min="1" placeholder="No limit" value={tier.maxPeople ?? ""} onChange={(e) => patchTier(index, "maxPeople", e.target.value === "" ? "" : Number(e.target.value))} />
                  </td>
                  <td>
                    <InputGroup>
                      <InputGroup.Text>Rs.</InputGroup.Text>
                      <Form.Control type="number" min="0" step="0.01" value={tier.amount ?? 0} onChange={(e) => patchTier(index, "amount", Number(e.target.value || 0))} />
                    </InputGroup>
                  </td>
                  <td>
                    <Form.Control type="number" value={tier.displayOrder ?? index + 1} onChange={(e) => patchTier(index, "displayOrder", Number(e.target.value || 0))} />
                  </td>
                  <td className="text-end">
                    <Button size="sm" variant="outline-danger" onClick={() => set("tiers", tiers.filter((_, idx) => idx !== index))}>
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="small text-muted mt-1">A group size no band covers is charged nothing - leave the last band's "To" blank for no limit.</div>
        </div>
      )}
    </div>
  );
}

function AmountField({ label, value, onChange }) {
  return (
    <div className="col-md-3">
      <Form.Label className="small text-muted mb-1">{label}</Form.Label>
      <InputGroup>
        <InputGroup.Text>Rs.</InputGroup.Text>
        <Form.Control type="number" min="0" step="0.01" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} />
      </InputGroup>
    </div>
  );
}

function Money({ label, value, onChange }) {
  return (
    <div className="col-md-2">
      <Form.Label>{label}</Form.Label>
      <InputGroup>
        <InputGroup.Text>Rs.</InputGroup.Text>
        <Form.Control type="number" min="0" step="0.01" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value || 0))} />
      </InputGroup>
    </div>
  );
}

const rupees = (value) => `Rs. ${Number(value || 0).toLocaleString("en-IN")}`;

function pricingSummary(pricing) {
  const type = pricing.pricingType || "GROUP_BANDS";
  if (type === "PER_PERSON") {
    const free = pricing.childrenFreeUpToAge != null && pricing.childrenFreeUpToAge !== ""
      ? `, children up to ${pricing.childrenFreeUpToAge} free`
      : "";
    return `${rupees(pricing.unitPrice)} per person${free}`;
  }
  if (type === "PER_VEHICLE") {
    const extra = Number(pricing.extraPeoplePerVehicle || 0) > 0
      ? `, up to ${pricing.extraPeoplePerVehicle} extra at ${rupees(pricing.extraPersonPrice)} each`
      : "";
    return `${rupees(pricing.unitPrice)} per vehicle of ${pricing.peoplePerVehicle || "?"}${extra}`;
  }
  if (type === "FIXED") return `${rupees(pricing.unitPrice)} per booking`;
  const tiers = pricing.tiers || [];
  if (!tiers.length) return "No bands";
  return tiers
    .map((tier) => {
      const range = tier.maxPeople ? `${tier.minPeople} to ${tier.maxPeople}` : `${tier.minPeople} or more`;
      return `${range}: ${rupees(tier.amount)}`;
    })
    .join(", ");
}

// An add-on saved before pricing types has none - it is edited as bands, the way it prices.
function forEditing(row) {
  const field = (value) => (value == null ? "" : value);
  const fields = (item) => ({
    pricingType: item.pricingType || "GROUP_BANDS",
    unitPrice: field(item.unitPrice),
    peoplePerVehicle: field(item.peoplePerVehicle),
    extraPeoplePerVehicle: field(item.extraPeoplePerVehicle),
    extraPersonPrice: field(item.extraPersonPrice),
    childrenFreeUpToAge: field(item.childrenFreeUpToAge),
  });
  return {
    ...blank,
    ...row,
    ...fields(row),
    tiers: row.tiers || [],
    options: (row.options || []).map((option) => ({ ...blankOption(option.displayOrder), ...option, ...fields(option) })),
  };
}

const numberOrNull = (value) => (value === "" || value == null ? null : Number(value));

const normalizeTiers = (tiers) =>
  (tiers || []).map((tier, index) => ({
    ...tier,
    minPeople: Number(tier.minPeople || 1),
    maxPeople: tier.maxPeople === "" || tier.maxPeople == null ? null : Number(tier.maxPeople),
    amount: Number(tier.amount || 0),
    displayOrder: Number(tier.displayOrder || index + 1),
  }));

const normalizePricing = (item) => ({
  unitPrice: numberOrNull(item.unitPrice),
  peoplePerVehicle: numberOrNull(item.peoplePerVehicle),
  extraPeoplePerVehicle: numberOrNull(item.extraPeoplePerVehicle),
  extraPersonPrice: numberOrNull(item.extraPersonPrice),
  childrenFreeUpToAge: numberOrNull(item.childrenFreeUpToAge),
});

function normalize(addon) {
  const type = addon.pricingType || "GROUP_BANDS";
  return {
    ...addon,
    contactName: String(addon.contactName || "").trim(),
    contactNumber: String(addon.contactNumber || "").trim(),
    googleMapLink: String(addon.googleMapLink || "").trim(),
    basePrice: Number(addon.basePrice || 0),
    sevenPersonPrice: Number(addon.sevenPersonPrice || 0),
    eightPlusPrice: Number(addon.eightPlusPrice || 0),
    subtractAmount: Number(addon.subtractAmount || 0),
    displayOrder: Number(addon.displayOrder || 0),
    active: addon.active !== false,
    // Bands on the add-on itself are saved without a type, so they price exactly as they always
    // have - base price fallback and the fixed "subtract when declined" included. With options the
    // add-on's own pricing is not used (or shown), so it is not sent either.
    pricingType: type === "GROUP_BANDS" || (addon.options || []).length ? null : type,
    ...normalizePricing(addon),
    tiers: normalizeTiers(addon.tiers),
    options: (addon.options || []).map((option, index) => ({
      ...option,
      name: String(option.name || "").trim(),
      description: String(option.description || "").trim(),
      pricingType: option.pricingType || "GROUP_BANDS",
      ...normalizePricing(option),
      tiers: normalizeTiers(option.tiers),
      contactName: String(option.contactName || "").trim(),
      contactNumber: String(option.contactNumber || "").trim(),
      googleMapLink: String(option.googleMapLink || "").trim(),
      displayOrder: Number(option.displayOrder ?? index + 1),
      active: option.active !== false,
    })),
  };
}
